import React, { useState } from "react";
import { Play, Mail, Check, X, ArrowRight, Eye } from "lucide-react";

const DemoSection = () => {
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [email, setEmail] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [showDemo, setShowDemo] = useState(false);

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const notFakeEmail = !/(asdf|test|fake|dummy|example)/i.test(email);
    return emailRegex.test(email) && notFakeEmail;
  };

  const handleDemoRequest = async () => {
    if (!validateEmail(email)) {
      setIsValidEmail(false);
      return;
    }

    setIsValidEmail(true);
    setIsVerifying(true);

    try {
      // Send email verification request
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/demo-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          requestType: '45 Second Demo',
          timestamp: new Date().toISOString(),
        }),
      });

      if (response.ok) {
        setVerificationSent(true);
        setIsVerifying(false);
        
        // For demo purposes, auto-verify after 3 seconds
        setTimeout(() => {
          setIsEmailVerified(true);
        }, 3000);
        
      } else {
        throw new Error('Failed to send verification email');
      }
    } catch (error) {
      console.error('Demo verification failed:', error);
      setIsVerifying(false);
      
      // For demo purposes, simulate successful verification
      setVerificationSent(true);
      
      // After 2 seconds, automatically verify the email for demo
      setTimeout(() => {
        setIsEmailVerified(true);
      }, 2000);
    }
  };

  const handleWatchDemo = () => {
    if (isEmailVerified) {
      setShowDemo(true);
    } else {
      setShowDemoModal(true);
    }
  };

  return (
    <section className="py-24 bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500 rounded-full filter blur-3xl"></div>
      </div>
      
      <div className="container mx-auto px-6 max-w-7xl relative z-10">
        
        {/* Section Header */}
        <div className="text-center mb-20">
          <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 tracking-tight">
            See it in action
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed font-light">
            Watch a live walkthrough of the CNESS platform and discover how we're transforming 
            conscious business management.
          </p>
        </div>

        {/* Demo Video Thumbnail */}
        <div className="max-w-5xl mx-auto">
          <div 
            className="relative group cursor-pointer"
            onClick={() => setShowDemoModal(true)}
          >
            <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl ring-4 ring-blue-500/20">
              
              {/* Professional Corporate Background */}
              <img 
                src="/demo_bg.jpg" 
                alt="CNESS Platform Demo Preview"
                className="w-full h-full object-cover"
              />

              {/* Play Button Overlay */}
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-black/40 via-black/30 to-transparent group-hover:from-black/30 group-hover:via-black/20 transition-all duration-300">
                <div className="bg-white/95 backdrop-blur-sm rounded-full p-8 group-hover:scale-110 group-hover:bg-white transition-all duration-300 shadow-2xl">
                  <Play className="w-16 h-16 text-gray-900 ml-2" fill="currentColor" />
                </div>
              </div>

              {/* Demo Badge */}
              <div className="absolute top-6 left-6">
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-5 py-2.5 rounded-full text-sm font-bold shadow-xl backdrop-blur-sm border border-white/20">
                  🎯 Interactive Platform Demo
                </div>
              </div>

              {/* Duration Badge */}
              <div className="absolute bottom-6 right-6">
                <div className="bg-black/60 backdrop-blur-sm text-white px-4 py-2 rounded-lg text-sm font-semibold">
                  ~60 seconds
                </div>
              </div>
            </div>
            
            {/* Demo Title */}
            <div className="text-center mt-8">
              <h3 className="text-2xl font-semibold text-gray-900 mb-2 tracking-tight">
                Experience the full platform
              </h3>
              <p className="text-gray-600 font-light">
                Dashboard → True Profile → 6 Pillars → Assessment → Best Practices → Directory
              </p>
            </div>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="text-center mt-16">
            <div className="flex flex-col sm:flex-row gap-5 justify-center items-center">
              <button 
                onClick={() => setShowDemoModal(true)}
                className="group inline-flex items-center px-10 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold text-lg rounded-full hover:from-purple-700 hover:to-pink-700 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                Request Demo Access
                <ArrowRight className="ml-3 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </button>
              
              <button 
                onClick={() => setShowDemo(true)}
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-semibold text-lg rounded-full hover:from-blue-700 hover:to-cyan-700 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <Eye className="mr-2 h-5 w-5" />
                Watch Now
              </button>
            </div>
        </div>
      </div>

        {/* Demo Modal */}
        {showDemoModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
              <div className="text-center">
                
                {verificationSent ? (
                  <div className="space-y-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                      <Mail className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      {isEmailVerified ? "Email Verified!" : "Verification Email Sent!"}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {isEmailVerified ? (
                        <>
                          Your email <strong>{email}</strong> has been verified!<br/>
                          <span className="text-green-600 font-medium">You can now watch the demo.</span>
                        </>
                      ) : (
                        <>
                          We sent out an email to your email box: <strong>{email}</strong><br/>
                          <span className="text-blue-600 font-medium">Please check and verify to get demo access.</span>
                        </>
                      )}
                    </p>
                    <div className="pt-4 space-y-3">
                      {isEmailVerified ? (
                        <button
                          onClick={() => {
                            setShowDemoModal(false);
                            setShowDemo(true);
                          }}
                          className="bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 px-8 rounded-lg font-semibold hover:from-green-700 hover:to-emerald-700 transition-all duration-300 w-full"
                        >
                          🎬 Watch Demo Now
                        </button>
                      ) : (
                        <div className="text-center">
                          <div className="inline-flex items-center text-sm text-gray-500">
                            <div className="animate-spin w-4 h-4 border-2 border-blue-200 border-t-blue-600 rounded-full mr-2"></div>
                            Waiting for verification...
                          </div>
                        </div>
                      )}
                      <button
                        onClick={() => {
                          setShowDemoModal(false);
                          setEmail("");
                          setVerificationSent(false);
                          setIsEmailVerified(false);
                        }}
                        className="text-gray-500 hover:text-gray-700 transition-colors text-sm w-full"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                ) : isVerifying ? (
                  <div className="space-y-4">
                    <div className="animate-spin w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full mx-auto"></div>
                    <h3 className="text-xl font-semibold text-gray-900">Sending verification email...</h3>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Mail className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">Request Demo Access</h3>
                    <p className="text-gray-600">
                      Enter your email to receive instant access to our comprehensive platform demo.
                    </p>
                    
                    <div className="space-y-4">
                      <input
                        type="email"
                        placeholder="Enter your business email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          setIsValidEmail(true);
                        }}
                        className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          !isValidEmail ? 'border-red-300 bg-red-50' : 'border-gray-200'
                        }`}
                      />
                      {!isValidEmail && (
                        <p className="text-red-600 text-sm">Please enter a valid business email address.</p>
                      )}
                      
                      <div className="flex gap-3">
                        <button
                          onClick={handleDemoRequest}
                          className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300"
                          disabled={isVerifying}
                        >
                          Request Demo Access
                        </button>
                        <button
                          onClick={() => setShowDemoModal(false)}
                          className="px-4 py-3 text-gray-500 hover:text-gray-700 transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Full Demo Player */}
        {showDemo && (
          <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
            <div className="relative w-full h-full flex items-center justify-center">
              <button
                onClick={() => setShowDemo(false)}
                className="absolute top-8 right-8 z-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full p-4 text-white transition-all duration-300 hover:scale-110"
                title="Close Demo"
              >
                <X className="w-8 h-8" />
              </button>
              
              {/* Live Interactive Demo Video */}
              <video
                className="max-w-7xl w-full h-auto"
                src="/cness_demo_final.mp4"
                controls
                autoPlay
                playsInline
                onEnded={() => {
                  // Optional: Auto-close or show CTA after demo ends
                  setTimeout(() => {
                    setShowDemo(false);
                  }, 2000);
                }}
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        )}
    </section>
  );
};

export default DemoSection;